import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { I } from '../icons.jsx';
import { Footer, AuthDecor, VividWordmark, FormAlert } from '../primitives.jsx';
import { api, errorMessage } from '../api.js';
import { storage } from '../storage.js';
import { handoffRedirect } from '../sso.js';

// Single-input 6-digit verification step. The backend logs the OTP to
// stdout in dev (see services.register), so the user copy-pastes it here
// while we do not yet have email delivery wired.
export default function VerifyOtpPage() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [pending, setPending] = useState(null);
  const [serverErr, setServerErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  // { ok: boolean, text: string } after a resend attempt.
  const [resendNote, setResendNote] = useState(null);

  useEffect(() => {
    const p = storage.getPendingVerify();
    if (!p) {
      navigate('/signup');
      return;
    }
    setPending(p);
  }, [navigate]);

  // The code lasts ten minutes. Without this the only advice on the page
  // was "ask your administrator".
  const resend = async () => {
    if (!pending?.email || resending) return;
    setResending(true);
    setResendNote(null);
    setServerErr('');
    try {
      await api.resendVerification(pending.email);
      setOtp('');
      setResendNote({ ok: true, text: `A new code is on its way to ${pending.email}. It replaces the earlier one.` });
    } catch (err) {
      setResendNote({ ok: false, text: errorMessage(err) });
    } finally {
      setResending(false);
    }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!pending || otp.length !== 6) return;
    setServerErr('');
    setLoading(true);
    try {
      const res = await api.verifyEmail({
        userId: pending.userId,
        otp,
        nextUrl: storage.getNext() || undefined,
      });
      storage.saveAuth({ token: res.token, refreshToken: res.refreshToken, user: res.user });
      storage.clearPendingVerify();
      handoffRedirect({
        token: res.token,
        refreshToken: res.refreshToken,
        redirectTo: res.redirectTo,
        navigate,
      });
    } catch (err) {
      setServerErr(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (!pending) return null;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="flex flex-col px-6 lg:px-12">
          <div className="h-20 flex items-center"><VividWordmark /></div>

          <div className="flex-1 flex items-start pt-[8vh] justify-center">
            <form onSubmit={submit} className="w-full max-w-[420px]" noValidate>
              <div className="inline-flex w-12 h-12 rounded-full bg-stone-100 items-center justify-center text-ink">
                <I.MailCheck size={22} />
              </div>
              {pending.otpUndelivered ? (
                <>
                  <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">Enter your verification code</h1>
                  <p role="alert" className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
                    Your account was created, but we couldn&apos;t email the code to{' '}
                    <span className="font-medium">{pending.email}</span>. Ask your administrator for
                    your 6-digit code and enter it below.
                    {import.meta.env.DEV && ' (Dev: the code is logged to the auth backend\'s stdout.)'}
                  </p>
                </>
              ) : pending.fromLogin ? (
                <>
                  <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">Verify your email to sign in</h1>
                  <p className="mt-2 text-[15px] text-stone-500">
                    Your password is correct, but <span className="text-ink font-medium">{pending.email}</span>{' '}
                    has not been verified yet.{' '}
                    {pending.resendFailed
                      ? 'Enter the 6-digit code from your sign-up email, or request a new one below.'
                      : 'We have sent a new 6-digit code. Enter it below to continue.'}
                  </p>
                </>
              ) : (
                <>
                  <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">Check your email</h1>
                  <p className="mt-2 text-[15px] text-stone-500">
                    We sent a 6-digit code to <span className="text-ink font-medium">{pending.email}</span>.
                    Enter it below to continue.
                  </p>
                </>
              )}

              <div className="mt-7">
                <label className="block mb-1.5 text-[12px] font-medium text-stone-700">
                  Verification code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="field-input text-center font-sans tabular-nums text-lg tracking-[0.3em]"
                  placeholder="••••••"
                  autoFocus
                />
                {!pending.otpUndelivered && (
                  <p className="mt-3 text-[12px] text-stone-500">
                    Did not get it? Check your spam folder. The code expires after 10 minutes.
                    {import.meta.env.DEV && " In dev the code is logged to the auth backend's stdout."}
                  </p>
                )}
                {pending.email && (
                  <button
                    type="button"
                    onClick={resend}
                    disabled={resending}
                    className="mt-2 text-[13px] font-medium text-ink underline underline-offset-2 hover:no-underline disabled:opacity-50"
                  >
                    {resending ? 'Sending…' : 'Send a new code'}
                  </button>
                )}
                {resendNote && (
                  <p
                    role="status"
                    className={`mt-2 text-[12px] ${resendNote.ok ? 'text-emerald-700' : 'text-red-600'}`}
                  >
                    {resendNote.text}
                  </p>
                )}
              </div>

              <div className="pt-7">
                <FormAlert message={serverErr} className="mb-4" />
                <button type="submit" disabled={otp.length !== 6 || loading} className="btn-primary w-full">
                  {loading ? (
                    <>
                      <svg role="status" aria-label="Loading" aria-busy="true" className="w-3.5 h-3.5 animate-spin" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.35" strokeWidth="2" fill="none" /><path d="M 8 2 a 6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" /></svg>
                      Verifying…
                    </>
                  ) : (
                    'Verify and continue'
                  )}
                </button>
                <button type="button" onClick={() => { storage.clearPendingVerify(); navigate(pending.fromLogin ? '/login' : '/signup'); }}
                  className="mt-3 w-full text-[13px] text-stone-500 hover:text-ink">
                  {pending.fromLogin ? '← Back to sign in' : '← Use a different email'}
                </button>
              </div>
            </form>
          </div>

          <Footer />
        </div>

        <div className="hidden lg:block"><AuthDecor /></div>
      </div>
    </div>
  );
}
